import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { UpcomingEventsScroller } from './components/UpcomingEventsScroller';
import { LandingPage } from './components/LandingPage';
import { FoundingFundersPopup } from './components/FoundingFundersPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NationalNetworkSection = lazy(() => import('./components/NationalNetworkSection').then(m => ({
  default: m.NationalNetworkSection
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const NationalNetworkPage = lazy(() => import('./pages/NationalNetworkPage').then(m => ({
  default: m.NationalNetworkPage
})));
const EncuentroCatrachoMiamiPage = lazy(() => import('./pages/EncuentroCatrachoMiamiPage').then(m => ({
  default: m.EncuentroCatrachoMiamiPage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
const HonduranIndependenceDayPage = lazy(() => import('./pages/HonduranIndependenceDayPage').then(m => ({
  default: m.HonduranIndependenceDayPage
})));
const VoicesOfImpactNetworkingPage = lazy(() => import('./pages/VoicesOfImpactNetworkingPage').then(m => ({
  default: m.VoicesOfImpactNetworkingPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="14xpxkq4usc1v">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="zwslmvafse8t">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="zzoiegajswvo" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="vvywxzp66vev" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="3gm7onf0o2feq" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="3i1sc9d9el93r" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="3868fkon0m6dy">
      <Navbar data-id="element-7" data-mp="3d49zfbs9m9qh" />
      <FoundingFundersPopup data-id="element-8" data-mp="3d40gu99jv4ut" />
      <main data-id="element-9" data-mp="3d38qpdyq1zbx">
        <Hero data-id="element-10" data-mp="3d2p5nodryuto" />
        <UpcomingEventsScroller data-id="element-11" data-mp="3d6czkon4tj8x" />
        <ConsularBanner data-id="element-12" data-mp="2zclnb8i0nl9i" />
        <Mission data-id="element-13" data-mp="2zcdathpt2hyu" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-15" data-mp="3456fy6p6rsbg" />} data-id="element-14" data-mp="344n7rhpc8iam">
          <OurPeopleStories data-id="element-16" data-mp="2pez8sn4mpvq2" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-18" data-mp="2pgsd3vrrs3uw" />} data-id="element-17" data-mp="2pga7w9n4qfau">
          <MakeDifferenceCTA data-id="element-19" data-mp="2ue21r97c4bho" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-20" data-mp="2uetzapkhsjal">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-21" data-mp="2ge9bfezpzxa8" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-23" data-mp="2kg4h0vooowtt" />} data-id="element-22" data-mp="2kfmi1ran04i7">
          <NewsBlog data-id="element-24" data-mp="2kfcppx24dl9c" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-26" data-mp="30vnbm1k4pece" />} data-id="element-25" data-mp="30v3sihiqsjws">
          <MediaScroller data-id="element-27" data-mp="30uveunespakj" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-29" data-mp="2vvr966r2xuyy" />} data-id="element-28" data-mp="2vwanldhiu188">
          <CommunityStrength data-id="element-30" data-mp="2vxlsjgmpdf0x" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-32" data-mp="2qwo9z2xs6iyw" />} data-id="element-31" data-mp="2qx7i5rxmpszq">
          <MissionPanels data-id="element-33" data-mp="2qvwdzr09uj83" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-35" data-mp="2lymhjregt7ar" />} data-id="element-34" data-mp="2ly4qd96ntj9p">
          <Leadership data-id="element-36" data-mp="2lxbddqh3xxj4" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-38" data-mp="2hw9fbcyxfriq" />} data-id="element-37" data-mp="2m0h234ltqxeb">
          <AdvisoryCouncil data-id="element-39" data-mp="2hxkqhxupc1jv" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-41" data-mp="2d62pxawlc2zf" />} data-id="element-40" data-mp="2hyceonlyyx04">
          <MakeDifferenceCTA data-id="element-42" data-mp="2d4rmjc724x8u" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-44" data-mp="286gd32kocor5" />} data-id="element-43" data-mp="2d3ybhz12flkw">
          <BusinessInvestments data-id="element-45" data-mp="287rfoz2dvqgo" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-47" data-mp="237vd949c4738" />} data-id="element-46" data-mp="284dc5nhzhb7n">
          <ConsularAffairs data-id="element-48" data-mp="238oanemelk8p" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-50" data-mp="1y8accoaltonv" />} data-id="element-49" data-mp="235sdvtmal1lu">
          <SocialMediaVoices data-id="element-51" data-mp="1y7h41ivzyr3m" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-53" data-mp="1u770ecvogcrx" />} data-id="element-52" data-mp="1yad0f2s757py">
          <NationalNetworkSection data-id="element-54" data-mp="1u7zx0l0x9lwc" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-56" data-mp="9pfmp1inbind" />} data-id="element-55" data-mp="9owg2gyg4gon">
          <HondurasQuiz data-id="element-57" data-mp="9o4ij0lag8vq" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-59" data-mp="dq7x3y397tl8" />} data-id="element-58" data-mp="9r8skelfpyub">
          <ExploreHonduras data-id="element-60" data-mp="dpgednur9qcd" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-62" data-mp="ipub49qwpdv3" />} data-id="element-61" data-mp="dscbxb2oyd0a">
          <FAQ data-id="element-63" data-mp="iqm7vnw8phmy" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-64" data-mp="inrmnu5ejssh">
        <Footer data-id="element-65" data-mp="np76jivuft9a" />
      </Suspense>
      <Suspense fallback={null} data-id="element-66" data-mp="no5k6i9dnkc9">
        <ChatbaseWidget data-id="element-67" data-mp="nmb8zvnwr6ld" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'network',
  'nonprofit-directory': 'network',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'network', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  // The site opens directly on the English main page; language is switched from
  // the toggle in the navigation bar rather than a separate entry screen.
  const [showLanding, setShowLanding] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showVoicesNetworking, setShowVoicesNetworking] = useState(() => window.location.hash === '#/voices-of-impact-ny-networking');
  const [showEncuentroMiami, setShowEncuentroMiami] = useState(() => window.location.hash === '#/encuentro-catracho-miami');
  const [showIndependenceDay, setShowIndependenceDay] = useState(() => window.location.hash === '#/independence-day');
  const [showNationalNetwork, setShowNationalNetwork] = useState(() => window.location.hash === '#/national-network');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      setShowVoicesNetworking(hash === '#/voices-of-impact-ny-networking');
      setShowEncuentroMiami(hash === '#/encuentro-catracho-miami');
      setShowIndependenceDay(hash === '#/independence-day');
      setShowNationalNetwork(hash === '#/national-network');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    // Language switching keeps the visitor on the current page.
    setShowLanding(false);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-69" data-mp="1ogr5r1l9szoj" />} data-id="element-68" data-mp="1tigjwq3shtgu">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-70" data-mp="1oi2950at05f4" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-71" data-mp="2h4da4c2bo8cg">
        <Suspense fallback={<SectionLoader data-id="element-73" data-mp="2c6buy280ot69" />} data-id="element-72" data-mp="2c6v7t4it8rdf">
          <ConsularPage data-id="element-74" data-mp="2c74zcwji76l8" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-75" data-mp="27dscorydx9v4">
        <Suspense fallback={<SectionLoader data-id="element-77" data-mp="2zwfv1zlzyasi" />} data-id="element-76" data-mp="2zvxpudhcwm8g">
          <BusinessMapPage data-id="element-78" data-mp="2zxqyfybj5gj3" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-79" data-mp="2v46a6xzqo0x3">
        <Suspense fallback={<SectionLoader data-id="element-81" data-mp="t8fhg89ilm8o" />} data-id="element-80" data-mp="t8yr71p0h4bm">
          <MembersDirectoryPage data-id="element-82" data-mp="ta9ul0ejoa27" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-83" data-mp="jidrtc1ijvrn">
        <Suspense fallback={<SectionLoader data-id="element-85" data-mp="oh77nb49vyuz" />} data-id="element-84" data-mp="jg1dd8uys3jc">
          <VoicesOfImpactNYPage data-id="element-86" data-mp="ogfavwyxvv34" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNetworking) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-87" data-mp="afuuf7ghybcs">
        <Suspense fallback={<SectionLoader data-id="element-89" data-mp="fhafgziochv6" />} data-id="element-88" data-mp="fgsl68fkodpw">
          <VoicesOfImpactNetworkingPage data-id="element-90" data-mp="fefth3k0iozn" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showEncuentroMiami) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-91" data-mp="5i358l78l3ca">
        <Suspense fallback={<SectionLoader data-id="element-93" data-mp="1ux9gzrxsdfmg" />} data-id="element-92" data-mp="5kfjoodscvkl">
          <EncuentroCatrachoMiamiPage data-id="element-94" data-mp="1ux16g6p4ymef" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showIndependenceDay) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-95" data-mp="1zvktkafgu48d">
        <Suspense fallback={<SectionLoader data-id="element-97" data-mp="3kf6c7qndifvd" />} data-id="element-96" data-mp="3kdmv5xtw80sj">
          <HonduranIndependenceDayPage data-id="element-98" data-mp="3kcbqzwwjcr0w" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNationalNetwork) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-99" data-mp="3u4pzr9m16xwk">
        <Suspense fallback={<SectionLoader data-id="element-101" data-mp="3pdrgq29nrlpm" />} data-id="element-100" data-mp="3u61358bke3n5">
          <NationalNetworkPage data-id="element-102" data-mp="3pcgck1cawbxz" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-103" data-mp="6xxclpafd7yk">
        <Suspense fallback={<SectionLoader data-id="element-105" data-mp="2x53cw1jz32a" />} data-id="element-104" data-mp="2yohafzql20w">
          <SaveTheDatePage data-id="element-106" data-mp="2zzlggx3gbsj" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-107" data-mp="byldr3sdb56i">
        <Suspense fallback={<SectionLoader data-id="element-109" data-mp="bwigpzsssbsa" />} data-id="element-108" data-mp="bz4nhx7v6n9g">
          <MemberProfilePage slug={memberSlug} data-id="element-110" data-mp="2iot0oskhaxlm" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-111" data-mp="2dr9k3m0b68qi">
        <Suspense fallback={<SectionLoader data-id="element-113" data-mp="1rxc25v4lq8y5" />} data-id="element-112" data-mp="1rwsfy27x4yeb">
          <NewsStoryPage postId={newsPostId} data-id="element-114" data-mp="1rw0xzu78uz6i" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-115" data-mp="1wu2gacwuiugx" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-116" data-mp="1wtizizixmc4h">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-117" data-mp="21n3x4qgm5435" />
      </LanguageProvider>
    </>;
}